import React, { useState } from "react";
import axios from "axios";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";

const FakturPajakReportPage = () => {
  const today = new Date();
  const [category, setCategory] = useState("");
  const [dateMode, setDateMode] = useState("month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [month, setMonth] = useState(String(today.getMonth() + 1).padStart(2, "0"));
  const [year, setYear] = useState(String(today.getFullYear()));
  const [rows, setRows] = useState([]);
  const [sheet, setSheet] = useState("Faktur");
  const [sheets, setSheets] = useState({ Faktur: { headers: [], data: [] }, DetailFaktur: { headers: [], data: [] } });
  const [appliedFilter, setAppliedFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const dates = dateMode === "month"
    ? {
        date_from: `${year}-${month}-01`,
        date_to: `${year}-${month}-${String(new Date(Number(year), Number(month), 0).getDate()).padStart(2, "0")}`,
      }
    : { date_from: dateFrom, date_to: dateTo };
  const params = { ...dates, ...(category ? { category } : {}) };
  const filterKey = JSON.stringify(params);
  const ready = appliedFilter === filterKey;
  const invalidCount = rows.filter((row) => row.issues?.length).length;

  const preview = async () => {
    if (!dates.date_from || !dates.date_to || dates.date_from > dates.date_to) {
      toast.error("Isi rentang tanggal yang benar");
      return;
    }
    setLoading(true);
    setAppliedFilter("");
    try {
      const response = await axios.get("/api/reports/faktur-pajak/preview", { params });
      setRows(response.data.data || []);
      setSheets({
        Faktur: { headers: response.data.faktur_headers || [], data: response.data.faktur || [] },
        DetailFaktur: { headers: response.data.detail_headers || [], data: response.data.detail || [] },
      });
      setAppliedFilter(filterKey);
    } catch (error) {
      setRows([]);
      toast.error(error.response?.data?.detail || "Gagal memuat preview");
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!ready || rows.length === 0) return;
    if (invalidCount > 0) {
      const examples = rows.filter((row) => row.issues?.length)
        .slice(0, 10).map((row) => `• ${row.invoice_number}: ${row.issues.join(", ")}`).join("\n");
      const confirmed = window.confirm(
        `${invalidCount} invoice memiliki data pajak yang perlu diperiksa:\n\n${examples}` +
        `${invalidCount > 10 ? "\n• ...dan invoice lainnya" : ""}` +
        "\n\nFile Excel akan tetap memuat semua invoice. Perbaiki data yang belum lengkap di Excel sebelum digunakan. Klik OK untuk download."
      );
      if (!confirmed) return;
    }
    setExporting(true);
    try {
      const response = await axios.get("/api/reports/faktur-pajak/export", {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Faktur_Pajak_${dates.date_from}_${dates.date_to}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Faktur Pajak berhasil didownload");
    } catch (error) {
      toast.error("Gagal mendownload Faktur Pajak. Periksa kembali preview.");
    } finally {
      setExporting(false);
    }
  };

  const display = (value) => {
    if (value == null) return "";
    if (typeof value === "number") return new Intl.NumberFormat("id-ID").format(value);
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) {
      const [year, month, day] = value.slice(0, 10).split("-");
      return `${month}-${day}-${year}`;
    }
    return String(value);
  };
  const currentSheet = sheets[sheet];
  const problemRows = rows.filter((row) => row.issues?.length);

  return (
    <div className="p-4 md:p-8 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Faktur Pajak</h1>
        <p className="text-gray-500 mt-1">Preview dan download Faktur Pajak untuk Invoice Export dan Domestic.</p>
      </div>

      <div className="bg-white rounded-xl border p-4 space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm font-medium">Jenis invoice
            <select className="block mt-1 border rounded-lg p-2 min-w-40" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Semua C dan D</option>
              <option value="EXPORT">Export C</option>
              <option value="DOMESTIC">Domestic D</option>
            </select>
          </label>
          <label className="text-sm font-medium">Periode
            <select className="block mt-1 border rounded-lg p-2" value={dateMode} onChange={(e) => setDateMode(e.target.value)}>
              <option value="month">Bulan dan tahun</option>
              <option value="range">Rentang tanggal</option>
            </select>
          </label>
          {dateMode === "month" ? (
            <>
              <label className="text-sm font-medium">Bulan
                <select className="block mt-1 border rounded-lg p-2" value={month} onChange={(e) => setMonth(e.target.value)}>
                  {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium">Tahun
                <input className="block mt-1 border rounded-lg p-2 w-24" type="number" min="2020" max="2100" value={year} onChange={(e) => setYear(e.target.value)} />
              </label>
            </>
          ) : (
            <>
              <label className="text-sm font-medium">Dari
                <input className="block mt-1 border rounded-lg p-2" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </label>
              <label className="text-sm font-medium">Sampai
                <input className="block mt-1 border rounded-lg p-2" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </label>
            </>
          )}
          <button type="button" onClick={preview} disabled={loading} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50">
            <Search size={16} /> {loading ? "Memuat..." : "Preview"}
          </button>
          <button type="button" onClick={download} disabled={!ready || rows.length === 0 || exporting} className="flex items-center gap-2 bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50">
            <Download size={16} /> {exporting ? "Menyiapkan..." : "Download Excel"}
          </button>
        </div>
        {ready && <p className="text-sm text-gray-600">{rows.length} invoice ditemukan. {invalidCount ? `${invalidCount} invoice perlu diperiksa; Anda bisa tetap download setelah konfirmasi, lalu memperbaikinya di Excel.` : "Data siap didownload."}</p>}
      </div>

      {ready && (
        <div className="space-y-4">
          {problemRows.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h2 className="font-bold text-red-800 mb-2">Data perlu dilengkapi ({problemRows.length})</h2>
              <ul className="text-sm text-red-700 space-y-1">
                {problemRows.map((row) => <li key={row.invoice_number}><strong>{row.invoice_number}</strong>: {row.issues.join(", ")}</li>)}
              </ul>
            </div>
          )}
          <div className="flex gap-2">
            {(["Faktur", "DetailFaktur"]).map((name) => (
              <button key={name} type="button" onClick={() => setSheet(name)} className={`px-4 py-2 rounded-lg ${sheet === name ? "bg-blue-600 text-white" : "bg-white border"}`}>{name}</button>
            ))}
          </div>
          <div className="bg-white rounded-xl border overflow-hidden">
          <div className="max-h-[600px] overflow-auto">
            <table className="w-max min-w-full text-sm">
              <thead className="sticky top-0 bg-gray-100 z-10">
                <tr>
                  {currentSheet.headers.map((heading, i) => (
                    <th key={i} className="p-3 text-left whitespace-nowrap">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={currentSheet.headers.length} className="p-8 text-center text-gray-500">Tidak ada invoice untuk filter ini.</td></tr>}
                {currentSheet.data.map((values, i) => (
                  <tr key={i} className={`border-t align-top ${rows[i]?.issues?.length ? "bg-red-50" : ""}`}>
                    {values.map((value, col) => <td key={col} className="p-3 whitespace-nowrap">{display(value)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FakturPajakReportPage;
